import { string, z } from "zod";

export const FormSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  steps: z.array(
    z.object({
      id: string(),
      name: string(),
      description: string().nullable(),
      order: z.number(),
      sections: z.array(
        z.object({
          id: string(),
          name: string(),
          title: string(),
          description: string().nullable(),
          fields: z.array(
            z.object({
              id: string(),
              name: string(),
              label: string(),
              type: z.enum([
                "text",
                "number",
                "file",
                "paragraph",
                "textarea",
                "select",
                "date",
                "dateRange",
                "dui_nit",
                "datetime_range",
                "date_range",
                "interoperability",
                "switch",
                "phone",
                "expedient",
                "filtered_select",
                "table",
              ]),
              required: z.boolean(),
              is_preventable: z.boolean().optional(),
              config: z.any().nullable(),
              filled_fields: z
                .array(
                  z.object({
                    id: string(),
                    label: z.string().optional(),
                    // Resolved expedient registry data, present only on
                    // filled_fields of expedient-type fields. See JER-446.
                    unique_code: z.string().optional().nullable(),
                    name: z.string().optional().nullable(),
                    created_at: z.iso.datetime(),
                    updated_at: z.iso.datetime(),
                    files: z.array(
                      z.object({
                        id: string(),
                        name: string(),
                        file_name: string(),
                        url: string(),
                        size_formatted: string(),
                      }),
                    ),
                    field_id: string(),
                    filled_form_id: string(),
                    correction: z.object({
                      id: string(),
                      status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                      revision: z.object({
                        id: string(),
                      }),
                      correction: z.object({
                        revision_id: string(),
                        group: z
                          .object({
                            id: string(),
                            name: string(),
                          })
                          .optional()
                          .nullable(),
                      }),
                      comments: z.array(
                        z.object({
                          text: z.string(),
                          created_by: z.object({
                            id: z.string(),
                          }),
                        }),
                      ),
                    }),
                    value: z
                      .string()
                      .or(z.number())
                      .or(z.boolean())
                      .or(z.array(z.string()))
                      .or(
                        z.object({
                          start: z.string(),
                          end: z.string(),
                          number: z.string(),
                          calling_code: z.string(),
                          country: z.string(),
                        }),
                      ),
                  }),
                )
                .nullable(),
            }),
          ),
        }),
      ),
    }),
  ),
  created_at: z.iso.datetime().nullable(),
  updated_at: z.iso.datetime().nullable(),
  deleted_at: z.iso.datetime().nullable(),
  form_assignments: z.array(
    z.object({
      id: z.string(),
      status: z.enum([
        "ASSIGNED",
        "IN_PROGRESS",
        "COMPLETED",
        "SUBMITTED",
        "CORRECTION",
        "FILL_CORRECTION",
        "FAILED_CORRECTION",
      ]),
      filled_form_id: z.string(),
      assigned_at: z.iso.datetime().nullable(),
      due_date: z.iso.datetime().nullable(),
      revisions: z.array(
        z.object({
          id: z.string(),
          status: z.string(),
          applicant_form_assignment_id: z.string().nullable(),
          internal_deadline: z.iso.datetime().nullable(),
          requires_leader_approval: z.boolean().optional(),
          groups: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            )
            .optional(),
          applicant_form_assignment: z
            .object({
              id: z.string(),
              status: z.string(),
            })
            .nullable(),
          corrections: z.array(
            z.object({
              id: z.string(),
              revision_id: z.string(),
              status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
              field_corrections: z.array(
                z.object({
                  id: z.string(),
                  correction_id: z.string(),
                  status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                  created_at: z.iso.datetime(),
                  comments: z.array(
                    z.object({
                      text: z.string(),
                      created_by: z.object({
                        id: z.string(),
                      }),
                      creator: z.object({
                        name: z.string(),
                        last_name: z.string(),
                      }),
                    }),
                  ),
                  filled_field: z
                    .object({
                      field_id: z.string(),
                      value: z.string().or(z.object()),
                    })
                    .optional(),
                  filled_field_id: z.string(),
                  filled_fields: z
                    .array(
                      z.object({
                        id: string(),
                        value: z
                          .string()
                          .or(z.number())
                          .or(z.boolean())
                          .or(z.array(z.string()))
                          .or(
                            z.object({
                              start: z.string(),
                              end: z.string(),
                            }),
                          ),
                      }),
                    )
                    .nullable(),
                }),
              ),
              group_id: z.string().nullable(),
              group: z
                .object({
                  id: z.string(),
                  name: z.string(),
                })
                .nullable()
                .optional(),
              is_leader_approved: z.boolean().optional(),
              comments: z.array(
                z.object({
                  text: z.string(),
                }),
              ),
            }),
          ),
          created_at: z.iso.datetime().optional(),
          updated_at: z.iso.datetime().optional(),
        }),
      ),
    }),
  ),
  user_form_assignments: z.array(
    z.object({
      id: z.string(),
      status: z.enum([
        "ASSIGNED",
        "IN_PROGRESS",
        "COMPLETED",
        "SUBMITTED",
        "CORRECTION",
        "FILL_CORRECTION",
        "FAILED_CORRECTION",
      ]),
      user_id: z.string().optional(),
      filled_form_id: z.string(),
      assigned_at: z.iso.datetime().nullable(),
      due_date: z.iso.datetime().nullable(),
      revisions: z.array(
        z.object({
          id: z.string(),
          status: z.string(),
          applicant_form_assignment_id: z.string().nullable(),
          internal_deadline: z.iso.datetime().nullable(),
          requires_leader_approval: z.boolean().optional(),
          groups: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
              }),
            )
            .optional(),
          applicant_form_assignment: z
            .object({
              id: z.string(),
              status: z.string(),
            })
            .nullable(),
          corrections: z.array(
            z.object({
              id: z.string(),
              revision_id: z.string(),
              field_corrections: z.array(
                z.object({
                  id: z.string(),
                  correction_id: z.string(),
                  status: z.enum(["APPROVED", "DENIED", "IN_PROCESS"]),
                  comments: z.array(
                    z.object({
                      text: z.string(),
                      created_by: z.object({
                        id: z.string(),
                      }),
                    }),
                  ),
                  filled_field_id: z.string(),
                  group: z
                    .object({
                      id: z.string(),
                      name: z.string(),
                    })
                    .nullable()
                    .optional(),
                  is_leader_approved: z.boolean().optional(),
                  filled_fields: z
                    .array(
                      z.object({
                        id: string(),
                        value: z
                          .string()
                          .or(z.number())
                          .or(z.boolean())
                          .or(z.array(z.string()))
                          .or(
                            z.object({
                              start: z.string(),
                              end: z.string(),
                            }),
                          ),
                      }),
                    )
                    .nullable(),
                }),
              ),
              group: z
                .object({
                  id: z.string(),
                  name: z.string(),
                })
                .nullable()
                .optional(),
              is_leader_approved: z.boolean().optional(),
              comments: z.array(
                z.object({
                  text: z.string(),
                }),
              ),
            }),
          ),
          created_at: z.iso.datetime().optional(),
          updated_at: z.iso.datetime().optional(),
        }),
      ),
    }),
  ),
  target_user_type: z.string().nullable(),
});

export type FormType = z.infer<typeof FormSchema>;

export type Form = z.infer<typeof FormSchema>;
export type Step = Form["steps"][number];
export type FilledField =
  Step["sections"][number]["fields"][number]["filled_fields"];
export type Section = Step["sections"][number];
export type Field = Section["fields"][number];

export const FormDetailResponseSchema = FormSchema;
export type FormDetailResponse = z.infer<typeof FormDetailResponseSchema>;

export const FORM_ASSIGNMENTS_STATUS = {
  ASSIGNED: "ASSIGNED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  SUBMITTED: "SUBMITTED",
  CORRECTION: "CORRECTION",
  FILL_CORRECTION: "FILL_CORRECTION",
  FAILED_CORRECTION: "FAILED_CORRECTION",
  PENDING: "PENDING",
} as const;
